/**
 * Утилиты для графиков
 */

/**
 * Форматирует дату для оси X графика
 * @param {string} dateStr - Дата в формате YYYY-MM-DD или YYYY-MM
 * @param {string} groupBy - Группировка (day/week/month)
 * @returns {string} Отформатированная дата
 */
export function formatChartDate(dateStr, groupBy = 'day') {
  if (!dateStr) return '';
  
  // Извлекаем дату из формата "Week 13 (2025-03-31)" или "2025-10" или "2025-10-13"
  let cleanDateStr = dateStr;
  if (dateStr.includes('(') && dateStr.includes(')')) {
    cleanDateStr = dateStr.match(/\(([^)]+)\)/)?.[1] || dateStr;
  }
  
  const date = new Date(cleanDateStr);
  
  // Проверка на невалидную дату
  if (isNaN(date.getTime())) {
    console.warn('Invalid date in formatChartDate:', dateStr, 'cleaned:', cleanDateStr);
    return dateStr; // Возвращаем как есть
  }
  
  const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
  
  if (groupBy === 'month') {
    // Для месяцев: "Сен.2025"
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${month}.${year}`;
  }
  
  if (groupBy === 'week') {
    // Для недель: "13 Ноя" (начало недели)
    const day = date.getDate();
    const month = months[date.getMonth()];
    return `${day} ${month}`;
  }
  
  // Для дней: "13 Ноя"
  const day = date.getDate();
  const month = months[date.getMonth()];
  return `${day} ${month}`;
}

/**
 * Форматирует дату для tooltip (полный формат)
 * @param {string} dateStr - Дата в формате YYYY-MM-DD или YYYY-MM
 * @param {string} groupBy - Группировка (day/week/month)
 * @returns {string} Отформатированная дата для tooltip
 */
export function formatTooltipDate(dateStr, groupBy = 'day') {
  if (!dateStr) return '';
  
  // Извлекаем дату из формата "Week 13 (2025-03-31)" или "2025-10" или "2025-10-13"
  let cleanDateStr = dateStr;
  if (dateStr.includes('(') && dateStr.includes(')')) {
    cleanDateStr = dateStr.match(/\(([^)]+)\)/)?.[1] || dateStr;
  }
  
  const date = new Date(cleanDateStr);
  
  // Проверка на невалидную дату
  if (isNaN(date.getTime())) {
    console.warn('Invalid date in formatTooltipDate:', dateStr, 'cleaned:', cleanDateStr);
    return dateStr; // Возвращаем как есть
  }
  
  const monthsFull = ['Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня', 'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'];
  
  if (groupBy === 'month') {
    // Для месяцев: "Сентябрь 2025"
    const month = monthsFull[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${year}`;
  }
  
  if (groupBy === 'week') {
    // Для недель: "13-19 Ноября 2025" (диапазон недели)
    const startDay = date.getDate();
    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + 6); // +6 дней = конец недели
    const endDay = endDate.getDate();
    const month = monthsFull[date.getMonth()];
    const year = date.getFullYear();
    return `${startDay}-${endDay} ${month} ${year}`;
  }
  
  // Для дней: "13 Ноября 2025"
  const day = date.getDate();
  const month = monthsFull[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Вычисляет интервал для тиков оси X
 * @param {number} dataLength - Количество точек данных
 * @param {string} groupBy - Группировка (day/week/month)
 * @returns {number} Интервал между тиками
 */
export function calculateTickInterval(dataLength, groupBy = 'day') {
  if (groupBy === 'month') {
    // Для месяцев всегда показываем все (interval=0 означает "показать все")
    return 0;
  }
  
  if (groupBy === 'week') {
    // Для недель показываем каждую 2-4 неделю
    return Math.max(1, Math.floor(dataLength / 15));
  }
  
  // Для дней показываем каждую 5-10 дату
  return Math.max(1, Math.floor(dataLength / 20));
}

/**
 * Настройки оси X для графиков
 * @param {string} groupBy - Группировка (day/week/month)
 * @param {number} dataLength - Количество точек данных
 * @returns {object} Настройки для XAxis компонента
 */
export function getXAxisConfig(groupBy = 'day', dataLength = 0) {
  // Для всех группировок - без наклона
  return {
    className: 'text-xs',
    tick: { fill: 'currentColor' },
    height: 60,
    interval: calculateTickInterval(dataLength, groupBy)
  };
}
